import { MouseEvent, useRef } from 'react';
import clsx from 'clsx';
import { useHeroIntroAnimation } from 'common/hook/useHeroIntroAnimation';
import { IProjectItem } from 'common/types/projects';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import useDimensions from 'use-element-dimensions';

import googlePlay from '../../../assets/image/appleStore.webp';
import appleStore from '../../../assets/image/googlePlay.webp';

interface Props {
  onImageClick: (src: string) => (e: MouseEvent<HTMLButtonElement>) => void;
  project: IProjectItem;
}

export const HeroProject: React.FC<Props> = ({ project, onImageClick }) => {
  const { t } = useTranslation('common');
  const el = useRef<HTMLElement | null>(null);
  const [containerWidth, ref] = useDimensions(entry => entry.contentRect.width);

  useHeroIntroAnimation({
    scopeRef: el,
    revealAnimations: [
      { delay: 1, selector: '#hero-image', toVars: { opacity: 1, translateY: 0 } },
      { delay: 1.4, selector: '#hero-website', toVars: { opacity: 1, translateY: 0 } },
    ],
  });

  const { heroImage, link } = project;
  let imageHeight = 0;
  if (heroImage) {
    const imageWidthMultiplier = heroImage.width / containerWidth;
    imageHeight = containerWidth
      ? Math.round(heroImage.height / imageWidthMultiplier)
      : heroImage.height;
  }
  const heightClass = `h-[${imageHeight || 320}px]`;
  return (
    <section ref={el} className="hero flex min-h-[70vh] flex-col justify-end 2xl:min-h-[60vh]">
      <div ref={ref} className="container pt-24 lg:pt-32">
        <h1
          id="hero-title"
          className="text-heading-hero max-w-[90vw] text-black lg:max-w-[1060px]"
          dangerouslySetInnerHTML={{ __html: project.title }}
        />
        {(!!link?.website || !!link?.appStore || !!link?.googlePlay) && (
          <div id="hero-website" className="mt-4 flex flex-row flex-wrap items-end opacity-0">
            {!!link?.website && (
              <a
                className="mb-[5px] mr-2 flex h-[48px] min-w-[160px] items-center justify-center rounded-lg bg-[#241F21] px-4 pb-1 text-xl text-white"
                href={link.website}
                target="blank">
                {t('visitWebsite')}
              </a>
            )}
            {!!link?.appStore && (
              <a className="mb-[5px] mr-2 font-bold" href={link.appStore} target="blank">
                <Image
                  id="header-logo"
                  className="relative h-[48px!important] w-auto object-contain"
                  src={appleStore}
                  alt="Nordit software development"
                  width={288}
                  height={96}
                />
              </a>
            )}
            {!!link?.googlePlay && (
              <a className="mb-[5px] mr-2 font-bold" href={link.googlePlay} target="blank">
                <Image
                  id="header-logo"
                  className="relative h-[48px!important] w-auto object-contain"
                  src={googlePlay}
                  alt="Nordit software development"
                  width={324}
                  height={96}
                />
              </a>
            )}
          </div>
        )}
      </div>
      <div className="container z-50 min-h-[200px] pt-24 lg:pt-32">
        <div
          id="hero-image"
          className={clsx(
            `relative z-50 -mb-4 min-h-[320px!important] w-full translate-y-12 opacity-0 lg:-mb-10 lg:mt-32`,
            {
              [heightClass]: !!imageHeight && imageHeight > 0,
              'drop-shadow-2xl': !!heroImage?.hasShadow,
            },
          )}>
          {!!heroImage?.src && (
            <button className="w-full" onClick={onImageClick(heroImage.src)}>
              <Image
                className={clsx('min-h-[350px!important] w-full object-cover object-bottom', {
                  [heightClass]: !!imageHeight && imageHeight > 0,
                })}
                alt={heroImage?.alt}
                loading="eager"
                src={heroImage?.src}
                height={heroImage.height}
                width={heroImage.width}
                sizes="100vw"
              />
            </button>
          )}
        </div>
      </div>
    </section>
  );
};
